// src/hooks/useRBAC.js
// Role-Based Access Control hook that mirrors the backend's permission checking logic

import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useIsIntegrationAssigned } from '../contexts/LicenseContext';

// Role hierarchy - lower value = higher permission
const ROLES = {
  OWNER: 1,
  ADMIN: 2,
  RISK_MANAGER: 3,
  CONTRIBUTOR: 4,
  APPROVER: 5,
  VIEWER: 6
};

// Scope hierarchy - for inheritance checks
// SELF (6) is the lowest scope: zero inherent visibility, access only via explicit assignment
const SCOPES = {
  ACCOUNT: 1,
  TENANT: 2,
  ORG: 3,
  BUSINESS_UNIT: 4,
  DEAL: 5,
  SELF: 6
};

// Convert string role/scope to numeric value
const getRoleValue = (role) => {
  if (typeof role === 'number') return role;
  return ROLES[role?.toUpperCase()] || 999;
};

const getScopeValue = (scope) => {
  if (typeof scope === 'number') return scope;
  return SCOPES[scope?.toUpperCase()] || 999;
};

// Mirrors backend/Models/RoleSemantics.cs. At DEAL / BUSINESS_UNIT scope the role value decides
// BREADTH, not just strength:
//   OWNER / ADMIN / RISK_MANAGER  manage the entity and see all of it
//   VIEWER                        sees all of it, read-only
//   CONTRIBUTOR / APPROVER        see only the items assigned to them within it
// So the hierarchy is not a total order at entity scope — CONTRIBUTOR (4) sees less than
// VIEWER (6) — and breadth must never be tested with a numeric `<= VIEWER` comparison there.
// At ORG / TENANT / ACCOUNT scope nothing changes. SELF has always been assignment-scoped.
const isAssignmentScopedRole = (role) => {
  const value = getRoleValue(role);
  return value === ROLES.CONTRIBUTOR || value === ROLES.APPROVER;
};

const hasEntityBreadth = (role) => !isAssignmentScopedRole(role);

export const useRBAC = () => {
  const { user, userRoles, orgHierarchy, dealHierarchy, businessUnitHierarchy, isSuperAdmin: superAdmin, isSupportAdmin: supportAdmin, authVersion } = useAuth();

  // Integration assignment status from LicenseContext
  const isIntegrationAssigned = useIsIntegrationAssigned();

  // Get the current organization from localStorage
  const getCurrentOrg = () => {
    try {
      const selectedOrg = localStorage.getItem('selectedOrganization');
      if (selectedOrg) {
        return JSON.parse(selectedOrg);
      }
    } catch (e) {}
    return null;
  };

  // Get hierarchy info for an organization
  const getOrgHierarchy = (orgId) => {
    return orgHierarchy.find(h => h.orgId === orgId) || null;
  };

  /**
   * Check if user has a role at or above the minimum required level,
   * at the specified scope or any parent scope.
   *
   * @param {string|number} minimumRole - The minimum role required (e.g., 'ADMIN' or 2)
   * @param {string|number} scope - The scope level (e.g., 'ORG' or 3)
   * @param {number} scopeEntityId - The entity ID at that scope (e.g., organizationId)
   * @returns {boolean}
   */
  const hasMinimumRoleAtOrAbove = (minimumRole, scope, scopeEntityId) => {
    if (!userRoles || userRoles.length === 0) {
      return false;
    }

    const minRoleValue = getRoleValue(minimumRole);
    const requestedScopeValue = getScopeValue(scope);
    const hierarchy = getOrgHierarchy(scopeEntityId);

    // Check each user role
    for (const userRole of userRoles) {
      const userRoleValue = getRoleValue(userRole.role);
      const userScopeValue = getScopeValue(userRole.scope);

      // User's role must be at or above the minimum (lower value = higher permission)
      if (userRoleValue > minRoleValue) {
        continue;
      }

      // Check if the scope matches or is a parent scope
      if (userScopeValue <= requestedScopeValue) {
        // User's scope is at or above the requested scope level
        // Now check if it applies to this entity

        // Direct match at the same scope
        if (userScopeValue === requestedScopeValue && userRole.scopeEntityId === scopeEntityId) {
          return true;
        }

        // Parent scope checks
        if (hierarchy) {
          // ORG scope requested, check if user has role at TENANT or ACCOUNT
          if (requestedScopeValue === SCOPES.ORG) {
            if (userScopeValue === SCOPES.TENANT && userRole.scopeEntityId === hierarchy.tenantId) {
              return true;
            }
            if (userScopeValue === SCOPES.ACCOUNT && userRole.scopeEntityId === hierarchy.accountId) {
              return true;
            }
          }

          // TENANT scope requested, check if user has role at ACCOUNT
          if (requestedScopeValue === SCOPES.TENANT) {
            if (userScopeValue === SCOPES.ACCOUNT && userRole.scopeEntityId === hierarchy.accountId) {
              return true;
            }
          }
        }

        // DEAL scope requested, check parent scopes via dealHierarchy
        if (requestedScopeValue === SCOPES.DEAL && dealHierarchy) {
          const dealH = dealHierarchy.find(d => d.dealId === scopeEntityId);
          if (dealH) {
            if (userScopeValue === SCOPES.ORG && userRole.scopeEntityId === dealH.orgId) return true;
            if (userScopeValue === SCOPES.TENANT && userRole.scopeEntityId === dealH.tenantId) return true;
            if (userScopeValue === SCOPES.ACCOUNT && userRole.scopeEntityId === dealH.accountId) return true;
          }
        }

        // BUSINESS_UNIT scope requested, check parent scopes via businessUnitHierarchy
        if (requestedScopeValue === SCOPES.BUSINESS_UNIT && businessUnitHierarchy) {
          const buH = businessUnitHierarchy.find(b => b.businessUnitId === scopeEntityId);
          if (buH) {
            if (userScopeValue === SCOPES.ORG && userRole.scopeEntityId === buH.orgId) return true;
            if (userScopeValue === SCOPES.TENANT && userRole.scopeEntityId === buH.tenantId) return true;
            if (userScopeValue === SCOPES.ACCOUNT && userRole.scopeEntityId === buH.accountId) return true;
          }
        }

        // If checking at ORG scope and hierarchy doesn't exist,
        // still allow if user has role at exact ORG scope
        if (userRole.scopeEntityId === scopeEntityId) {
          return true;
        }
      }
    }

    return false;
  };

  /**
   * Check if user can administer the current organization.
   * Requires ADMIN role at ORG scope or above.
   *
   * @param {number} orgId - Optional org ID, defaults to current organization
   * @returns {boolean}
   */
  const canAdministerOrganization = (orgId) => {
    const org = getCurrentOrg();
    const targetOrgId = orgId || org?.organizationId;

    if (!targetOrgId) {
      return false;
    }

    return hasMinimumRoleAtOrAbove('ADMIN', 'ORG', parseInt(targetOrgId));
  };

  /**
   * Check if user can create/edit/archive remediation workflow templates (Settings → Workflows).
   * Mirrors the backend CanManageOrganizationRisks gate on the template endpoints
   * (RemediationWorkflowsController): RISK_MANAGER+ at ORG scope or above. Deal/BU-scoped
   * managers and assignment-based access are NOT included (task 19; was ADMIN+ before).
   *
   * @param {number} orgId - Optional org ID, defaults to current organization
   * @returns {boolean}
   */
  const canManageWorkflowTemplates = (orgId) => canManageOrganizationRisks(orgId);

  /**
   * Check if user can view (minimum VIEWER role).
   * Also allows DEAL/BU-scoped and SELF-scoped users for their org.
   */
  const canViewOrganization = (orgId) => {
    const org = getCurrentOrg();
    const targetOrgId = orgId || org?.organizationId;

    if (!targetOrgId) {
      return false;
    }

    const parsedOrgId = parseInt(targetOrgId);

    if (hasMinimumRoleAtOrAbove('VIEWER', 'ORG', parsedOrgId)) {
      return true;
    }

    // DEAL-scoped access: verify deal belongs to this org via hierarchy.
    // The `<= VIEWER` here is intentionally trivially-true — this asks "does the user hold ANY
    // deal role in this org", i.e. membership, which is the right test for org access. Do not
    // "fix" it into a breadth check; that would lock assignment-scoped members out of the org
    // entirely. Mirrors backend CanAccessOrganization.
    if (dealHierarchy?.some(d => d.orgId === parsedOrgId) &&
        userRoles?.some(r => getScopeValue(r.scope) === SCOPES.DEAL && getRoleValue(r.role) <= ROLES.VIEWER)) {
      return true;
    }

    // BU-scoped access: membership, same as the deal branch above.
    if (businessUnitHierarchy?.some(b => b.orgId === parsedOrgId) &&
        userRoles?.some(r => getScopeValue(r.scope) === SCOPES.BUSINESS_UNIT && getRoleValue(r.role) <= ROLES.VIEWER)) {
      return true;
    }

    // SELF-scoped: verify ScopeEntityId matches the requested org
    if (userRoles?.some(r => getScopeValue(r.scope) === SCOPES.SELF && r.scopeEntityId === parsedOrgId && getRoleValue(r.role) <= ROLES.APPROVER)) {
      return true;
    }

    return false;
  };

  /**
   * Check if user can assess a given assessment.
   * Only the assigned user can assess; unassigned assessments cannot be assessed by anyone.
   * The assigned user must have CONTRIBUTOR+ role at ORG, DEAL/BU, or SELF scope.
   *
   * @param {number} assignedToUserId - The user ID the assessment is assigned to
   * @param {number} orgId - Optional org ID, defaults to current organization
   * @param {string} scope - Optional scope (e.g. 'DEAL', 'BUSINESS_UNIT')
   * @param {number} scopeEntityId - Optional scope entity ID
   * @param {number[]} assigneeUserIds - Optional multi-respondent assignee user ids
   * @returns {boolean}
   */
  const canAssess = (assignedToUserId, orgId, scope, scopeEntityId, assigneeUserIds = []) => {
    // Unassigned assessments cannot be assessed by anyone
    if (!assignedToUserId && assigneeUserIds.length === 0) return false;

    // Must be the assigned owner OR one of the multi-respondent assignees
    const currentUserId = user?.id;
    const isAssigned = currentUserId &&
      (assignedToUserId === currentUserId || assigneeUserIds.includes(currentUserId));
    if (!isAssigned) return false;

    // Verify CONTRIBUTOR+ access to this org at any scope
    const targetOrgId = orgId || getCurrentOrg()?.organizationId;
    if (!targetOrgId) return false;

    if (hasMinimumRoleAtOrAbove('CONTRIBUTOR', 'ORG', parseInt(targetOrgId))) return true;
    if (scope && scopeEntityId && hasMinimumRoleAtOrAbove('CONTRIBUTOR', scope, scopeEntityId)) return true;
    if (canAssessViaAssignment(assignedToUserId, assigneeUserIds)) return true;

    return false;
  };

  /**
   * Get a user-friendly reason why the assess action is disabled, or null if allowed.
   *
   * @param {number} assignedToUserId - The user ID the assessment is assigned to
   * @param {number} orgId - Optional org ID, defaults to current organization
   * @param {string} scope - Optional scope (e.g. 'DEAL', 'BUSINESS_UNIT')
   * @param {number} scopeEntityId - Optional scope entity ID
   * @param {number[]} assigneeUserIds - Optional multi-respondent assignee user ids
   * @returns {string|null}
   */
  const getAssessDisabledReason = (assignedToUserId, orgId, scope, scopeEntityId, assigneeUserIds = []) => {
    if (!assignedToUserId && assigneeUserIds.length === 0)
      return 'Assessment must be assigned before it can be assessed';

    const currentUserId = user?.id;
    const isAssigned = currentUserId &&
      (assignedToUserId === currentUserId || assigneeUserIds.includes(currentUserId));
    if (!isAssigned) return 'Only an assigned user can assess this';

    const targetOrgId = orgId || getCurrentOrg()?.organizationId;
    if (!targetOrgId) return "You don't have permission to assess this";

    if (hasMinimumRoleAtOrAbove('CONTRIBUTOR', 'ORG', parseInt(targetOrgId))) return null;
    if (scope && scopeEntityId && hasMinimumRoleAtOrAbove('CONTRIBUTOR', scope, scopeEntityId)) return null;
    if (canAssessViaAssignment(assignedToUserId, assigneeUserIds)) return null;

    return "You don't have permission to assess this";
  };

  /**
   * Get the deal IDs the user can access for a specific org.
   * Returns null if the user has ORG+ scope for that org (no filter needed).
   *
   * @param {number} orgId - Optional org ID, defaults to current organization
   */
  const getAccessibleDealIds = (orgId) => {
    if (!userRoles || userRoles.length === 0) return [];

    const org = getCurrentOrg();
    const targetOrgId = orgId || org?.organizationId;
    if (!targetOrgId) return [];
    const parsedOrgId = parseInt(targetOrgId);

    // Only return null if user has ORG+ scope for THIS specific org
    if (hasMinimumRoleAtOrAbove('VIEWER', 'ORG', parsedOrgId)) {
      return null;
    }

    // Collect DEAL-scoped entity IDs that belong to this org
    const dealIdsForOrg = new Set(
      (dealHierarchy || []).filter(d => d.orgId === parsedOrgId).map(d => d.dealId)
    );

    // Breadth roles only — a deal held as CONTRIBUTOR/APPROVER is not browsable (see
    // isAssignmentScopedRole). Every caller uses this as a list filter. For "am I on this deal
    // at all", use getMemberDealIds.
    return [...new Set(
      userRoles
        .filter(r => getScopeValue(r.scope) === SCOPES.DEAL
                     && dealIdsForOrg.has(r.scopeEntityId)
                     && hasEntityBreadth(r.role))
        .map(r => r.scopeEntityId)
    )];
  };

  /**
   * Get the business unit IDs the user can access for a specific org.
   * Returns null if the user has ORG+ scope for that org (no filter needed).
   *
   * @param {number} orgId - Optional org ID, defaults to current organization
   */
  const getAccessibleBusinessUnitIds = (orgId) => {
    if (!userRoles || userRoles.length === 0) return [];

    const org = getCurrentOrg();
    const targetOrgId = orgId || org?.organizationId;
    if (!targetOrgId) return [];
    const parsedOrgId = parseInt(targetOrgId);

    // Only return null if user has ORG+ scope for THIS specific org
    if (hasMinimumRoleAtOrAbove('VIEWER', 'ORG', parsedOrgId)) {
      return null;
    }

    // Collect BU-scoped entity IDs that belong to this org
    const buIdsForOrg = new Set(
      (businessUnitHierarchy || []).filter(b => b.orgId === parsedOrgId).map(b => b.businessUnitId)
    );

    // Breadth roles only — see getAccessibleDealIds.
    return [...new Set(
      userRoles
        .filter(r => getScopeValue(r.scope) === SCOPES.BUSINESS_UNIT
                     && buIdsForOrg.has(r.scopeEntityId)
                     && hasEntityBreadth(r.role))
        .map(r => r.scopeEntityId)
    )];
  };

  /**
   * Check if user can view a specific deal
   *
   * @param {number} dealId - The deal ID to check
   * @param {number} orgId - Optional org ID, defaults to current organization
   */
  const canViewDeal = (dealId, orgId) => {
    const accessible = getAccessibleDealIds(orgId);
    if (accessible === null) return true; // ORG+ scope sees everything
    return accessible.includes(dealId);
  };

  /**
   * Check if user can view a specific business unit
   *
   * @param {number} buId - The business unit ID to check
   * @param {number} orgId - Optional org ID, defaults to current organization
   */
  const canViewBusinessUnit = (buId, orgId) => {
    const accessible = getAccessibleBusinessUnitIds(orgId);
    if (accessible === null) return true; // ORG+ scope sees everything
    return accessible.includes(buId);
  };

  const isSuperAdmin = useCallback(() => !!superAdmin, [superAdmin]);
  const isSupportAdmin = useCallback(() => !!supportAdmin, [supportAdmin]);
  // Super admin only: the Fortinet "Diagnostic tracing (24 h)" switch (POST /fortinet/trace).
  // Mirrors backend CanManageFortinetTracing; Support admin does not qualify.
  const canManageFortinetTracing = useCallback(() => isSuperAdmin(), [isSuperAdmin]);

  // Super admin only: the Delete button on every assessment list (all 7 flows) and the Enterprise
  // "Deleted assessments" list. Mirrors backend CanDeleteAssessments; Support admin does not qualify.
  const canDeleteAssessments = useCallback(() => !!superAdmin, [superAdmin]);

  /**
   * Check if the user's only role(s) for this org are SELF-scoped
   * (i.e., no ACCOUNT/TENANT/ORG/DEAL/BU roles grant access to this org).
   * @param {number} orgId - Optional org ID, defaults to current organization
   * @returns {boolean}
   */
  const isSelfScopeOnly = (orgId) => {
    if (!userRoles || userRoles.length === 0) return false;
    const org = getCurrentOrg();
    const targetOrgId = orgId || org?.organizationId;
    if (!targetOrgId) return false;
    const parsedOrgId = parseInt(targetOrgId);

    // ORG+ scope = full access
    if (hasMinimumRoleAtOrAbove('VIEWER', 'ORG', parsedOrgId)) return false;

    // DEAL-scoped role for a deal in this org = NOT self-scope-only
    if (dealHierarchy?.some(d => d.orgId === parsedOrgId) &&
        userRoles.some(r => getScopeValue(r.scope) === SCOPES.DEAL))
      return false;

    // BU-scoped role for a BU in this org = NOT self-scope-only
    if (businessUnitHierarchy?.some(b => b.orgId === parsedOrgId) &&
        userRoles.some(r => getScopeValue(r.scope) === SCOPES.BUSINESS_UNIT))
      return false;

    return userRoles.some(r =>
      getScopeValue(r.scope) === SCOPES.SELF && r.scopeEntityId === parsedOrgId
    );
  };

  /**
   * Check if user has a SELF-scoped role at or above the minimum level for a specific org.
   * @param {string|number} minimumRole - The minimum role required
   * @param {number} orgId - Optional org ID, defaults to current organization
   * @returns {boolean}
   */
  const hasSelfScopeRole = (minimumRole, orgId) => {
    if (!userRoles || userRoles.length === 0) return false;
    const org = getCurrentOrg();
    const targetOrgId = orgId || org?.organizationId;
    if (!targetOrgId) return false;
    const parsedOrgId = parseInt(targetOrgId);
    const minRoleValue = getRoleValue(minimumRole);
    return userRoles.some(r =>
      getScopeValue(r.scope) === SCOPES.SELF && r.scopeEntityId === parsedOrgId && getRoleValue(r.role) <= minRoleValue
    );
  };

  /**
   * Mirrors backend IUserRoleContext.HasAssignmentScopedRole. True when the user reaches work in
   * this org through assignment rather than scope: a SELF role, or an assignment-scoped
   * CONTRIBUTOR/APPROVER at DEAL or BU scope. Use this — not hasSelfScopeRole — for anything
   * gated on "what I've been assigned": a deal-scoped contributor holds no SELF role.
   *
   * @param {string|number} minimumRole
   * @param {number} orgId - Optional org ID, defaults to current organization
   * @returns {boolean}
   */
  const hasAssignmentScopedRole = (minimumRole, orgId) => {
    if (!userRoles || userRoles.length === 0) return false;
    const org = getCurrentOrg();
    const targetOrgId = orgId || org?.organizationId;
    if (!targetOrgId) return false;
    const parsedOrgId = parseInt(targetOrgId);
    const minRoleValue = getRoleValue(minimumRole);

    if (hasSelfScopeRole(minimumRole, parsedOrgId)) return true;

    const dealIdsForOrg = new Set(
      (dealHierarchy || []).filter(d => d.orgId === parsedOrgId).map(d => d.dealId)
    );
    const buIdsForOrg = new Set(
      (businessUnitHierarchy || []).filter(b => b.orgId === parsedOrgId).map(b => b.businessUnitId)
    );

    return userRoles.some(r => {
      const scopeValue = getScopeValue(r.scope);
      const inOrg = (scopeValue === SCOPES.DEAL && dealIdsForOrg.has(r.scopeEntityId))
                 || (scopeValue === SCOPES.BUSINESS_UNIT && buIdsForOrg.has(r.scopeEntityId));
      return inOrg && isAssignmentScopedRole(r.role) && getRoleValue(r.role) <= minRoleValue;
    });
  };

  /**
   * True when the user has NO breadth anywhere in this org — every role they hold here is
   * assignment-scoped (SELF at any role, or CONTRIBUTOR/APPROVER at DEAL/BU). These users have
   * nothing to browse: their work reaches them through My Tasks.
   *
   * Supersedes the old `isSelfScopeOnly() && !hasSelfScopeRole('RISK_MANAGER')` guard, which it
   * reproduces exactly for SELF-only users while also catching deal/BU contributors.
   *
   * @param {number} orgId - Optional org ID, defaults to current organization
   * @returns {boolean}
   */
  const isAssignmentScopedOnly = (orgId) => {
    if (!userRoles || userRoles.length === 0) return false;
    const org = getCurrentOrg();
    const targetOrgId = orgId || org?.organizationId;
    if (!targetOrgId) return false;
    const parsedOrgId = parseInt(targetOrgId);

    // Any ORG+ role is breadth.
    if (hasMinimumRoleAtOrAbove('VIEWER', 'ORG', parsedOrgId)) return false;

    const dealIdsForOrg = new Set(
      (dealHierarchy || []).filter(d => d.orgId === parsedOrgId).map(d => d.dealId)
    );
    const buIdsForOrg = new Set(
      (businessUnitHierarchy || []).filter(b => b.orgId === parsedOrgId).map(b => b.businessUnitId)
    );

    const rolesInOrg = userRoles.filter(r => {
      const scopeValue = getScopeValue(r.scope);
      return (scopeValue === SCOPES.DEAL && dealIdsForOrg.has(r.scopeEntityId))
          || (scopeValue === SCOPES.BUSINESS_UNIT && buIdsForOrg.has(r.scopeEntityId))
          || (scopeValue === SCOPES.SELF && r.scopeEntityId === parsedOrgId);
    });

    if (rolesInOrg.length === 0) return false;

    // SELF is assignment-scoped whatever the role value; DEAL/BU depends on the role.
    return rolesInOrg.every(r =>
      getScopeValue(r.scope) === SCOPES.SELF || isAssignmentScopedRole(r.role)
    );
  };

  /**
   * Check if user can create a Deal or Business Unit.
   * Mirrors backend CanCreateDealOrBU: ORG+ RISK_MANAGER check OR SELF-scoped RISK_MANAGER fallback.
   */
  const canCreateDealOrBusinessUnit = (orgId) => {
    const org = getCurrentOrg();
    const targetOrgId = orgId || org?.organizationId;
    if (!targetOrgId) return false;
    const parsedOrgId = parseInt(targetOrgId);
    // ORG+ scope-based check (mirrors backend CanManageRisks)
    if (canManageOrganizationRisks(targetOrgId)) return true;
    // SELF-scoped RISK_MANAGER+ can create deals/BUs in their org
    return hasSelfScopeRole('RISK_MANAGER', parsedOrgId);
  };

  /**
   * Check if user can create a Vendor.
   * Mirrors canCreateDealOrBusinessUnit: ORG+ RISK_MANAGER check OR SELF-scoped RISK_MANAGER fallback.
   */
  const canCreateVendor = (orgId) => {
    const org = getCurrentOrg();
    const targetOrgId = orgId || org?.organizationId;
    if (!targetOrgId) return false;
    const parsedOrgId = parseInt(targetOrgId);
    if (canManageOrganizationRisks(targetOrgId)) return true;
    return hasSelfScopeRole('RISK_MANAGER', parsedOrgId);
  };

  /**
   * Check if user has ADMIN+ role at any DEAL or BUSINESS_UNIT scope
   * for entities belonging to the specified organization.
   * Mirrors backend CanAdministerAnyDealOrBusinessUnit.
   *
   * @param {number} orgId - Optional org ID, defaults to current organization
   * @returns {boolean}
   */
  const canAdministerAnyDealOrBusinessUnit = (orgId) => {
    const org = getCurrentOrg();
    const targetOrgId = orgId || org?.organizationId;
    if (!targetOrgId) return false;
    const parsedOrgId = parseInt(targetOrgId);

    // Check DEAL-scoped ADMIN+ roles for deals in this org
    const dealIdsForOrg = new Set(
      (dealHierarchy || []).filter(d => d.orgId === parsedOrgId).map(d => d.dealId)
    );
    if (userRoles?.some(r =>
      getScopeValue(r.scope) === SCOPES.DEAL &&
      getRoleValue(r.role) <= ROLES.ADMIN &&
      dealIdsForOrg.has(r.scopeEntityId)
    )) return true;

    // Check BU-scoped ADMIN+ roles for BUs in this org
    const buIdsForOrg = new Set(
      (businessUnitHierarchy || []).filter(b => b.orgId === parsedOrgId).map(b => b.businessUnitId)
    );
    return userRoles?.some(r =>
      getScopeValue(r.scope) === SCOPES.BUSINESS_UNIT &&
      getRoleValue(r.role) <= ROLES.ADMIN &&
      buIdsForOrg.has(r.scopeEntityId)
    ) || false;
  };

  /**
   * Check if a SELF-scoped user can assess via assignment relationship.
   * @param {number} assignedToUserId - The user ID the assessment is assigned to
   * @param {number[]} assigneeUserIds - Optional multi-respondent assignee user ids
   * @returns {boolean}
   */
  const canAssessViaAssignment = (assignedToUserId, assigneeUserIds = []) => {
    if (!hasAssignmentScopedRole('CONTRIBUTOR')) return false;
    const currentUserId = user?.id;
    if (!currentUserId) return false;
    return (assignedToUserId && assignedToUserId === currentUserId) ||
           assigneeUserIds.includes(currentUserId);
  };

  /**
   * Check if a SELF-scoped user can view a risk via assignment.
   * @param {number} assessmentAssigneeId - The assessment assignee user ID
   * @param {number} riskAssigneeId - The risk assignee user ID
   * @returns {boolean}
   */
  const canViewRiskViaAssignment = (assessmentAssigneeId, riskAssigneeId) => {
    if (!hasAssignmentScopedRole('CONTRIBUTOR')) return false;
    const currentUserId = user?.id;
    if (!currentUserId) return false;
    return (assessmentAssigneeId && assessmentAssigneeId === currentUserId) ||
           (riskAssigneeId && riskAssigneeId === currentUserId);
  };

  /**
   * Check if a SELF-scoped user can update a risk via assignment.
   * @param {number} riskAssigneeId - The risk assignee user ID
   * @returns {boolean}
   */
  const canUpdateRiskViaAssignment = (riskAssigneeId) => {
    if (!hasAssignmentScopedRole('CONTRIBUTOR')) return false;
    const currentUserId = user?.id;
    return currentUserId && riskAssigneeId && riskAssigneeId === currentUserId;
  };

  /**
   * Check if a SELF-scoped user can approve a risk via assignment as approver.
   * @param {number} primaryApproverId - The primary approver user ID
   * @param {number} secondaryApproverId - The secondary approver user ID
   * @returns {boolean}
   */
  const canApproveRiskViaAssignment = (primaryApproverId, secondaryApproverId) => {
    if (!hasAssignmentScopedRole('APPROVER')) return false;
    const currentUserId = user?.id;
    if (!currentUserId) return false;
    return (primaryApproverId && primaryApproverId === currentUserId) ||
           (secondaryApproverId && secondaryApproverId === currentUserId);
  };

  /**
   * Check if user can view Organization Assessments.
   * Only users with ORG+ scope (ORG, TENANT, or ACCOUNT) can view org assessments.
   * SELF-scoped, DEAL-scoped, and BU-scoped users cannot (they access assigned ones via MyTasks).
   *
   * @param {number} orgId - Optional org ID, defaults to current organization
   * @returns {boolean}
   */
  const canViewOrganizationAssessments = (orgId) => {
    const org = getCurrentOrg();
    const targetOrgId = orgId || org?.organizationId;
    if (!targetOrgId) return false;
    return hasMinimumRoleAtOrAbove('VIEWER', 'ORG', parseInt(targetOrgId));
  };

  /**
   * Check if user can manage risks for the current organization.
   * Requires RISK_MANAGER role at ORG scope or above.
   *
   * @param {number} orgId - Optional org ID, defaults to current organization
   * @returns {boolean}
   */
  const canManageOrganizationRisks = (orgId) => {
    const org = getCurrentOrg();
    const targetOrgId = orgId || org?.organizationId;
    if (!targetOrgId) return false;
    return hasMinimumRoleAtOrAbove('RISK_MANAGER', 'ORG', parseInt(targetOrgId));
  };

  /**
   * Check if the user explicitly holds the OWNER role at DEAL scope for a specific deal.
   * Used for deal privacy controls (make private/public, manage access) — deliberately
   * does NOT inherit from ORG/TENANT/ACCOUNT scopes: private deals are owner-managed.
   *
   * @param {number} dealId - The deal ID to check
   * @returns {boolean}
   */
  const isDealOwner = (dealId) => {
    if (!userRoles || userRoles.length === 0 || !dealId) return false;
    const parsedDealId = parseInt(dealId);
    return userRoles.some(r =>
      getScopeValue(r.scope) === SCOPES.DEAL &&
      getRoleValue(r.role) === ROLES.OWNER &&
      r.scopeEntityId === parsedDealId
    );
  };

  /**
   * Check if the user explicitly holds the OWNER or ADMIN role at DEAL scope for a specific
   * deal. Like isDealOwner, this deliberately does NOT inherit from ORG/TENANT/ACCOUNT
   * scopes — private-deal management is granted per deal.
   *
   * @param {number} dealId - The deal ID to check
   * @returns {boolean}
   */
  const isDealAdmin = (dealId) => {
    if (!userRoles || userRoles.length === 0 || !dealId) return false;
    const parsedDealId = parseInt(dealId);
    return userRoles.some(r =>
      getScopeValue(r.scope) === SCOPES.DEAL &&
      (getRoleValue(r.role) === ROLES.OWNER || getRoleValue(r.role) === ROLES.ADMIN) &&
      r.scopeEntityId === parsedDealId
    );
  };

  /**
   * Check if user can manage risks for a specific deal.
   * Requires RISK_MANAGER role at DEAL scope or above.
   *
   * @param {number} dealId - The deal ID to check
   * @returns {boolean}
   */
  const canManageDeal = (dealId) => {
    return hasMinimumRoleAtOrAbove('RISK_MANAGER', 'DEAL', dealId);
  };

  /**
   * Check if user can manage at least one deal in the org — ORG+ RISK_MANAGER, or a
   * DEAL-scoped RISK_MANAGER+ for any deal belonging to this org. Used to gate the
   * "Add Deal" control (the deal dropdown itself is already scoped to viewable deals).
   *
   * @param {number} orgId - Optional org ID, defaults to current organization
   */
  const canManageAnyDeal = (orgId) => {
    const targetOrgId = orgId || getCurrentOrg()?.organizationId;
    if (!targetOrgId) return false;
    const parsedOrgId = parseInt(targetOrgId);
    if (canManageOrganizationRisks(parsedOrgId)) return true;
    const dealIdsForOrg = new Set(
      (dealHierarchy || []).filter(d => d.orgId === parsedOrgId).map(d => d.dealId)
    );
    return userRoles?.some(r =>
      getScopeValue(r.scope) === SCOPES.DEAL &&
      getRoleValue(r.role) <= ROLES.RISK_MANAGER &&
      dealIdsForOrg.has(r.scopeEntityId)
    ) || false;
  };

  /**
   * Check if user can manage risks for a specific business unit.
   * Requires RISK_MANAGER role at BUSINESS_UNIT scope or above.
   *
   * @param {number} buId - The business unit ID to check
   * @returns {boolean}
   */
  const canManageBusinessUnit = (buId) => {
    return hasMinimumRoleAtOrAbove('RISK_MANAGER', 'BUSINESS_UNIT', buId);
  };

  /**
   * Check if user can manage at least one business unit in the org — ORG+ RISK_MANAGER,
   * or a BU-scoped RISK_MANAGER+ for any BU belonging to this org. Used to gate the
   * "Add Business Unit" control.
   *
   * @param {number} orgId - Optional org ID, defaults to current organization
   */
  const canManageAnyBusinessUnit = (orgId) => {
    const targetOrgId = orgId || getCurrentOrg()?.organizationId;
    if (!targetOrgId) return false;
    const parsedOrgId = parseInt(targetOrgId);
    if (canManageOrganizationRisks(parsedOrgId)) return true;
    const buIdsForOrg = new Set(
      (businessUnitHierarchy || []).filter(b => b.orgId === parsedOrgId).map(b => b.businessUnitId)
    );
    return userRoles?.some(r =>
      getScopeValue(r.scope) === SCOPES.BUSINESS_UNIT &&
      getRoleValue(r.role) <= ROLES.RISK_MANAGER &&
      buIdsForOrg.has(r.scopeEntityId)
    ) || false;
  };

  /**
   * Check if user can manage Security Insights (policy documents, GRC connectors).
   * Requires RISK_MANAGER+ at ORG scope only — does NOT grant access to DEAL/BU-scoped users.
   * Mirrors backend CanManageSecurityInsights.
   *
   * @param {number} orgId - Optional org ID, defaults to current organization
   * @returns {boolean}
   */
  const canManageSecurityInsights = (orgId) => {
    const org = getCurrentOrg();
    const targetOrgId = orgId || org?.organizationId;
    if (!targetOrgId) return false;
    return hasMinimumRoleAtOrAbove('RISK_MANAGER', 'ORG', parseInt(targetOrgId));
  };

  /**
   * Check if user can manage Workforce Intelligence (the WIE forecast dashboard).
   * Requires the "Enterprise Risk Manager" role — i.e. RISK_MANAGER+ at ORG scope only;
   * does NOT grant access to DEAL/BU-scoped users. Mirrors backend CanManageWorkforceIntelligence.
   *
   * @param {number} orgId - Optional org ID, defaults to current organization
   * @returns {boolean}
   */
  const canManageWorkforceIntelligence = (orgId) => {
    const org = getCurrentOrg();
    const targetOrgId = orgId || org?.organizationId;
    if (!targetOrgId) return false;
    return hasMinimumRoleAtOrAbove('RISK_MANAGER', 'ORG', parseInt(targetOrgId));
  };

  /**
   * Check if user can manage integrations (Jira, ServiceNow, Security Insights).
   * Combines scope-based access (RISK_MANAGER+ at ORG/DEAL/BU) with
   * assignment-based access (SELF-scoped CONTRIBUTOR+ with IntegrationAssignment).
   * Mirrors backend CanManageIntegrationsAsync.
   *
   * @param {number} orgId - Optional org ID, defaults to current organization
   * @returns {boolean}
   */
  const canManageIntegrations = (orgId) => {
    const org = getCurrentOrg();
    const targetOrgId = orgId || org?.organizationId;
    if (!targetOrgId) return false;
    const parsedOrgId = parseInt(targetOrgId);

    // ORG+ scope RISK_MANAGER can manage
    if (canManageOrganizationRisks(parsedOrgId)) return true;

    // DEAL-scoped RISK_MANAGER+
    if (dealHierarchy?.some(d => d.orgId === parsedOrgId) &&
        userRoles?.some(r => getScopeValue(r.scope) === SCOPES.DEAL && getRoleValue(r.role) <= ROLES.RISK_MANAGER))
      return true;

    // BU-scoped RISK_MANAGER+
    if (businessUnitHierarchy?.some(b => b.orgId === parsedOrgId) &&
        userRoles?.some(r => getScopeValue(r.scope) === SCOPES.BUSINESS_UNIT && getRoleValue(r.role) <= ROLES.RISK_MANAGER))
      return true;

    // Assignment-based: SELF-scoped CONTRIBUTOR+ with assignment record
    if (hasSelfScopeRole('CONTRIBUTOR') && isIntegrationAssigned) return true;

    return false;
  };

  return {
    hasMinimumRoleAtOrAbove,
    canAdministerOrganization,
    // Remediation workflow templates (RISK_MANAGER+ at ORG; mirrors CanManageOrganizationRisks)
    canManageWorkflowTemplates,
    canViewOrganization,
    canAssess,
    getAssessDisabledReason,
    isSuperAdmin,
    canManageFortinetTracing,
    isSupportAdmin,
    canDeleteAssessments,
    getAccessibleDealIds,
    getAccessibleBusinessUnitIds,
    canViewDeal,
    canViewBusinessUnit,
    // SELF scope helpers
    isSelfScopeOnly,
    hasSelfScopeRole,
    // Assignment-scoped access (SELF, or CONTRIBUTOR/APPROVER at DEAL/BU)
    hasAssignmentScopedRole,
    isAssignmentScopedOnly,
    canCreateDealOrBusinessUnit,
    canCreateVendor,
    canAdministerAnyDealOrBusinessUnit,
    canViewOrganizationAssessments,
    // Security Insights management
    canManageSecurityInsights,
    // Workforce Intelligence management (Enterprise Risk Manager — RISK_MANAGER+ at ORG)
    canManageWorkforceIntelligence,
    // Unified integration management (scope-based + assignment-based)
    canManageIntegrations,
    // Deal privacy / ownership
    isDealOwner,
    isDealAdmin,
    // Convenience risk-management checks
    canManageOrganizationRisks,
    canManageDeal,
    canManageBusinessUnit,
    canManageAnyDeal,
    canManageAnyBusinessUnit,
    // Assignment-based access (for SELF-scoped users)
    canAssessViaAssignment,
    canViewRiskViaAssignment,
    canUpdateRiskViaAssignment,
    canApproveRiskViaAssignment,
    // Agent access
    canRunAgent: (orgId) => {
      const org = getCurrentOrg();
      const targetOrgId = orgId || org?.organizationId;
      if (!targetOrgId) return false;
      return hasMinimumRoleAtOrAbove('VIEWER', 'ORG', parseInt(targetOrgId));
    },
    // Org-wide AI estate aggregates (summary, assets, spend, changes, usage, posture, graph and the
    // org-wide action lists). Mirrors backend CanViewAllAiEstateData: the platform-admin flags
    // admit, otherwise the same ORG-level rule as canRunAgent. canRunAgent itself is NOT widened:
    // it still gates Navigation, the Ask drawer, SOC and the Portfolio / Data leaks tabs.
    canViewAllAiEstateData: (orgId) => {
      if (superAdmin || supportAdmin) return true;
      const org = getCurrentOrg();
      const targetOrgId = orgId || org?.organizationId;
      if (!targetOrgId) return false;
      return hasMinimumRoleAtOrAbove('VIEWER', 'ORG', parseInt(targetOrgId));
    },
    // Org-wide GRC data: the NERC CIP sites / assets and patch management panels and the GRC
    // Frameworks tab's requirement view. Mirrors backend CanViewAllGrcData: the platform-admin flags
    // admit, otherwise VIEWER+ at ORG scope or above, with no Deal/BU/Self fallback.
    canViewAllGrcData: (orgId) => {
      if (superAdmin || supportAdmin) return true;
      const org = getCurrentOrg();
      const targetOrgId = orgId || org?.organizationId;
      if (!targetOrgId) return false;
      return hasMinimumRoleAtOrAbove('VIEWER', 'ORG', parseInt(targetOrgId));
    },
    userRoles,
    authVersion,
    ROLES,
    SCOPES
  };
};

export default useRBAC;
